/**
 * Generates JSON-LD structured data for video series/episodes
 */
export interface VideoEpisode {
  title: string;
  videoId: string;
  thumbnail: string;
  publishedAt: string;
  description: string;
  channelTitle: string;
}

export interface StructuredDataOptions {
  seriesName: string;
  seriesDescription?: string;
  episodes: VideoEpisode[];
  channelUrl?: string;
}

export function generateVideoSeriesStructuredData(
  options: StructuredDataOptions
): object {
  const { seriesName, seriesDescription, episodes, channelUrl } = options;

  return {
    "@context": "https://schema.org",
    "@type": "VideoSeries",
    name: seriesName,
    description: seriesDescription || `Watch ${seriesName} episodes`,
    ...(channelUrl && {
      publisher: {
        "@type": "Organization",
        name: "PIWC Leicester",
        url: channelUrl,
      },
    }),
    video: episodes.map((episode) => ({
      "@type": "VideoObject",
      name: episode.title,
      description: episode.description,
      thumbnailUrl: episode.thumbnail,
      uploadDate: episode.publishedAt,
      contentUrl: `https://www.youtube.com/watch?v=${episode.videoId}`,
      embedUrl: `https://www.youtube.com/embed/${episode.videoId}`,
    })),
  };
}

export function injectStructuredData(
  data: object,
  id: string = "structured-data"
) {
  if (typeof document === "undefined") return;

  // Remove existing script with same ID
  const existing = document.getElementById(id);
  if (existing) {
    existing.remove();
  }

  // Create new script tag
  const script = document.createElement("script");
  script.id = id;
  script.type = "application/ld+json";
  script.textContent = JSON.stringify(data);
  document.head.appendChild(script);
}
